// src/components/inventory/PurchaseOrderCreateModal.js
//
// --- UPDATE (Procurement MOW+ v1) ----------------------------------------------------------
//   • FlowSense checks while you build the order: each line shows stock, what is already on order,
//     the FlowSense suggested quantity, days of cover after delivery and the price paid before;
//     duplicates, price increases and failing suppliers are warned about on the Review step.
//   • Supplier record (grade, real lead time) and "use this date" for the expected delivery.
//   • Auto-Draft Memo works offline: when the AI engine is down, FlowSense writes the memo from
//     real usage, stock and lead time (no invented numbers). Budget votes used before are offered.
//   • Prefill from Smart Reorder / alerts: initialItems, initialSupplierId, initialPriority.
//   • Fixes: line edits mutated state objects in place; the submit error showed a generic
//     message — it now shows the server's reason; { data } responses for suppliers / items.
// --- END UPDATE ---------------------------------------------------------------------------
import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  Button, FormControl, FormLabel, Input, Select, VStack, HStack, IconButton,
  useToast, useColorModeValue, Text, NumberInput, NumberInputField, Box,
  SimpleGrid, Divider, Icon, Tooltip, Badge, Flex, Textarea,
  Stepper, Step, StepIndicator, StepStatus, StepIcon, StepNumber,
  StepTitle, StepSeparator, useSteps, FormErrorMessage, Alert, AlertIcon, AlertDescription
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { MdShoppingCart, MdDateRange, MdStore, MdAttachMoney, MdNote, MdCheckCircle } from 'react-icons/md';
import { FaRobot } from 'react-icons/fa';
import supplierService from '../../services/supplierService';
import inventoryService from '../../services/inventoryService';
import purchaseOrderService from '../../services/purchaseOrderService';
import aiService from '../../services/aiService';
import { motion, AnimatePresence } from 'framer-motion';
// --- UPDATE (Procurement MOW+ v1)
import { MdAutoAwesome } from 'react-icons/md';
import { poErrorMessage } from '../../services/purchaseOrderService';
import inventoryIntelApi from '../../services/inventoryIntelApi';
import FlowSenseInsightBar from './FlowSenseInsightBar';
import { localJustification, gradeMeta, usd, explainProcurementForMinistry } from './procurementAiIntel';
// --- END UPDATE

const WIZARD_STEPS = [
  { title: 'Vendor', description: 'Supplier & Dates' },
  { title: 'Items', description: 'Order Contents' },
  { title: 'Budget', description: 'Funding & Notes' },
  { title: 'Review', description: 'Confirm Order' },
];

const EMPTY_PO_DATA = {
  supplier_id: '',
  order_date: new Date().toISOString().split('T')[0],
  expected_delivery_date: '',
  budget_code: '',
  priority: 'medium',
  notes: '',
};

const PurchaseOrderCreateModal = ({ isOpen, onClose, onSave, initialItems = null, initialSupplierId = null, initialPriority = null }) => { // --- UPDATE: + prefill props
  // --- STATE ---
  const [poData, setPoData] = useState(EMPTY_PO_DATA);
  const [items, setItems] = useState([{ item_id: '', quantity: 1, unit_price: 0.00 }]);
  const [suppliers, setSuppliers] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);

  const toast = useToast();
  const { activeStep, setActiveStep } = useSteps({ index: 0, count: WIZARD_STEPS.length });

  // --- THEME ---
  const modalBg = useColorModeValue("white", "gray.800");
  const sectionBg = useColorModeValue("gray.50", "gray.750");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const headerGradient = useColorModeValue('linear(to-r, blue.500, teal.500)', 'linear(to-r, blue.400, teal.400)');
  const highlightColor = useColorModeValue("blue.50", "blue.900");
  // --- UPDATE
  const aiBg = useColorModeValue("teal.50", "whiteAlpha.100");
  const warnBg = useColorModeValue("orange.50", "rgba(221,107,32,0.15)");
  const [check, setCheck] = useState(null);
  const [insights, setInsights] = useState(null);
  // --- END UPDATE

  // --- DATA LOADING ---
  useEffect(() => {
    if (isOpen) {
      const fetchData = async () => {
        try {
          const [suppliersRes, inventoryRes] = await Promise.all([
            supplierService.getAllSuppliers(),
            inventoryService.getAllItems(),
          ]);
          setSuppliers(Array.isArray(suppliersRes) ? suppliersRes : suppliersRes?.data || []); // --- UPDATE: { data } too
          setInventoryItems(Array.isArray(inventoryRes) ? inventoryRes : inventoryRes?.data || []); // --- UPDATE
        } catch (error) {
          toast({ title: 'Error fetching system data.', status: 'error' });
        }
      };
      fetchData();
      
      // Reset state on open
      setPoData(EMPTY_PO_DATA);
      setItems([{ item_id: '', quantity: 1, unit_price: 0.00 }]);
      setFormErrors({});
      setActiveStep(0);
      // --- UPDATE: prefill + FlowSense inventory insight
      if (initialSupplierId || initialPriority) setPoData((p) => ({ ...p, supplier_id: initialSupplierId ? String(initialSupplierId) : p.supplier_id, priority: initialPriority || p.priority }));
      if (Array.isArray(initialItems) && initialItems.length) setItems(initialItems.map((i) => ({ item_id: String(i.item_id), quantity: i.quantity || 1, unit_price: i.unit_price ?? 0 })));
      setCheck(null);
      inventoryIntelApi.overview().then((o) => setInsights(o.items || [])).catch(() => setInsights(null));
      // --- END UPDATE
    }
  }, [isOpen, toast, setActiveStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- UPDATE (Procurement MOW+ v1): live FlowSense check of the draft
  useEffect(() => {
    if (!isOpen) return undefined;
    const lines = items.filter((i) => i.item_id).map((i) => ({ item_id: Number(i.item_id), quantity: Number(i.quantity) || 0, unit_price: Number(i.unit_price) }));
    if (!poData.supplier_id && !lines.length) { setCheck(null); return undefined; }
    const t = setTimeout(() => { purchaseOrderService.checkDraft({ supplier_id: poData.supplier_id || null, items: lines }).then(setCheck).catch(() => setCheck(null)); }, 450);
    return () => clearTimeout(t);
  }, [isOpen, poData.supplier_id, items]);
  const checkByItem = useMemo(() => new Map((check?.lines || []).map((l) => [String(l.item_id), l])), [check]);
  // --- END UPDATE

  // --- HANDLERS ---
  const updateField = (field, value) => {
    setPoData(prev => ({ ...prev, [field]: value }));
    setFormErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const handleItemChange = (index, e) => {
    const newItems = [...items];
    const { name, value } = e.target;
    newItems[index] = { ...newItems[index], [name]: value }; // --- UPDATE: copy (was mutating the state object)
    
    if (name === 'item_id') {
      const selectedItem = inventoryItems.find(inv => String(inv.id) === String(value));
      if (selectedItem) {
        newItems[index].unit_price = selectedItem.unit_price || 0.00;
      }
    }
    setItems(newItems);
  };
  
  const handleItemNumberChange = (index, value, name) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [name]: value }; // --- UPDATE: copy
    setItems(newItems);
  };

  const addItem = () => setItems([...items, { item_id: '', quantity: 1, unit_price: 0.00 }]);
  const removeItem = (index) => setItems(items.filter((_, i) => i !== index));

  const cartTotal = useMemo(() => {
    return items.reduce((acc, item) => acc + (parseFloat(item.quantity) * parseFloat(item.unit_price || 0)), 0);
  }, [items]);

  // --- AI CO-PILOT ---
  const handleGenerateAIJustification = async () => {
    const primaryItem = items.find(i => i.item_id && i.quantity > 0);
    
    if (!primaryItem) {
      toast({ title: 'Item Required', description: 'Add an inventory item to generate a contextual justification.', status: 'warning' });
      return;
    }

    setIsGeneratingAI(true);
    try {
      const response = await aiService.generatePOJustification(primaryItem.item_id, primaryItem.quantity);
      if (response.success) {
        const existingNotes = poData.notes ? `${poData.notes}\n\n` : '';
        updateField('notes', `${existingNotes}--- AI Justification ---\n${response.draft}`);
        toast({ title: '✨ Memo Generated', status: 'success', duration: 3000 });
      }
    } catch (error) {
      // --- UPDATE: AI engine offline → FlowSense memo from real usage / stock / lead time
      const memo = localJustification({ supplierName: suppliers.find((x) => String(x.id) === String(poData.supplier_id))?.name, budgetCode: poData.budget_code, priority: poData.priority, total: cartTotal,
        items: items.filter((i) => i.item_id).map((i) => ({ name: inventoryItems.find((x) => String(x.id) === String(i.item_id))?.name, quantity: i.quantity, unit_price: i.unit_price, insight: (insights || []).find((x) => String(x.id) === String(i.item_id)) })) });
      if (memo) {
        const existingNotes = poData.notes ? `${poData.notes}\n\n` : '';
        updateField('notes', `${existingNotes}--- FlowSense Justification ---\n${memo}`);
        toast({ title: 'FlowSense memo added', description: 'The AI engine is offline — the memo was built from inventory records.', status: 'info', duration: 4000 });
      } else toast({ title: 'AI Generation Failed', status: 'error' });
      // --- END UPDATE
    } finally {
      setIsGeneratingAI(false);
    }
  };

  // --- WIZARD NAVIGATION & VALIDATION ---
  const validateStep = (step) => {
    const errs = {};
    if (step === 0) {
      if (!poData.supplier_id) errs.supplier_id = 'Please select a supplier.';
      if (!poData.order_date) errs.order_date = 'Order date is required.';
    }
    if (step === 1) {
      items.forEach((item, i) => {
        if (!item.item_id) errs[`item_${i}`] = 'Item selection required.';
        if (item.quantity < 1) errs[`qty_${i}`] = 'Quantity must be at least 1.';
      });
    }
    if (step === 2) {
      if (!poData.budget_code) errs.budget_code = 'Budget vote code is required for encumbrance.';
    }
    
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    } else {
      toast({ title: 'Validation Error', description: 'Please complete all required fields.', status: 'error', duration: 2000 });
    }
  };

  const handleBack = () => setActiveStep(prev => prev - 1);

  // --- SUBMIT ---
  const handleSubmit = async () => {
    if (!validateStep(3)) return; // Final sanity check
    
    setIsSubmitting(true);
    try {
      // Align payload to backend expectations
      const payloadItems = items.map(i => ({
        item_id: parseInt(i.item_id, 10),
        quantity: parseInt(i.quantity, 10),
        quoted_price: parseFloat(i.unit_price),
        unit_price: parseFloat(i.unit_price)
      }));

      await purchaseOrderService.createPO(poData, payloadItems);
      toast({ title: 'Purchase Order Issued', description: 'The order has been routed for Financial Approval.', status: 'success' });
      onSave();
      onClose();
    } catch (error) {
      toast({ title: 'Submission Failed', description: poErrorMessage(error), status: 'error' }); // --- UPDATE: server reason
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- RENDER CONTENT BY STEP ---
  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Vendor
        return (
          <VStack spacing={5} align="stretch" as={motion.div} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <FormControl isRequired isInvalid={!!formErrors.supplier_id}>
              <FormLabel fontWeight="bold">Authorized Supplier</FormLabel>
              <Select name="supplier_id" placeholder="— Select supplier —" value={poData.supplier_id} onChange={(e) => updateField('supplier_id', e.target.value)} bg={modalBg} borderRadius="xl" size="lg">
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Select>
              <FormErrorMessage>{formErrors.supplier_id}</FormErrorMessage>
            </FormControl>
            {/* --- UPDATE: supplier record + delivery date from the real lead time */}
            {check?.supplier && (
              <Box p={3} bg={aiBg} borderRadius="xl" fontSize="sm">
                <HStack spacing={2} flexWrap="wrap"><Icon as={MdAutoAwesome} color="teal.400" /><Badge colorScheme={gradeMeta(check.supplier.supplier_grade).color}>{check.supplier.supplier_grade || 'unrated'}</Badge><Text>{check.supplier.grade_reason}</Text></HStack>
                {check.lead_time_days !== null && <HStack mt={1}><Text>Lead time {check.lead_time_days} days ({check.lead_time_source}) → {check.suggested_delivery_date}</Text>
                  <Button size="xs" variant="outline" colorScheme="teal" onClick={() => updateField('expected_delivery_date', check.suggested_delivery_date)}>Use this date</Button></HStack>}
              </Box>
            )}

            <SimpleGrid columns={2} spacing={4}>
              <FormControl isRequired isInvalid={!!formErrors.order_date}>
                <FormLabel fontWeight="bold">Order Date</FormLabel>
                <Input type="date" value={poData.order_date} onChange={(e) => updateField('order_date', e.target.value)} bg={modalBg} borderRadius="xl" />
                <FormErrorMessage>{formErrors.order_date}</FormErrorMessage>
              </FormControl>
              <FormControl>
                <FormLabel fontWeight="bold">Expected Delivery</FormLabel>
                <Input type="date" value={poData.expected_delivery_date} onChange={(e) => updateField('expected_delivery_date', e.target.value)} bg={modalBg} borderRadius="xl" />
              </FormControl>
            </SimpleGrid>

            <FormControl>
              <FormLabel fontWeight="bold">Priority Level</FormLabel>
              <Select value={poData.priority} onChange={(e) => updateField('priority', e.target.value)} bg={modalBg} borderRadius="xl">
                <option value="low">Low (Routine Restock)</option>
                <option value="medium">Medium (Standard Need)</option>
                <option value="high">High (Urgent Requirement)</option>
                <option value="critical">Critical (Infrastructure Emergency)</option>
              </Select>
            </FormControl>
          </VStack>
        );

      case 1: // Items
        return (
          <VStack spacing={4} align="stretch" as={motion.div} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <Flex justify="space-between" align="center">
              <Text fontWeight="bold" color="gray.600">Line Items</Text>
              <Badge colorScheme="blue" borderRadius="full" px={3} py={1} fontSize="md">
                Total: ${cartTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </Badge>
            </Flex>
            
            {items.map((item, index) => (
              <Box key={index} p={4} bg={sectionBg} borderRadius="xl" borderWidth="1px" borderColor={formErrors[`item_${index}`] ? 'red.300' : borderColor}>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="xs" fontWeight="bold" color="gray.500">ITEM {index + 1}</Text>
                  {items.length > 1 && (
                    <IconButton icon={<DeleteIcon />} size="xs" variant="ghost" colorScheme="red" onClick={() => removeItem(index)} aria-label={`Remove line ${index + 1}`} />
                  )}
                </HStack>
                <SimpleGrid columns={{ base: 1, md: 12 }} spacing={3}>
                  <FormControl isRequired gridColumn={{ md: "span 6" }}>
                    <Select name="item_id" placeholder="Select catalog item..." value={item.item_id} onChange={(e) => handleItemChange(index, e)} bg={modalBg} borderRadius="md">
                      {inventoryItems.map(inv => <option key={inv.id} value={inv.id}>{inv.name} (Stock: {inv.quantity})</option>)}
                    </Select>
                  </FormControl>
                  <FormControl isRequired gridColumn={{ md: "span 3" }} isInvalid={!!formErrors[`qty_${index}`]}>
                    <NumberInput min={1} value={item.quantity} onChange={(val) => handleItemNumberChange(index, val, 'quantity')}>
                      <NumberInputField bg={modalBg} borderRadius="md" placeholder="Qty" />
                    </NumberInput>
                  </FormControl>
                  <FormControl isRequired gridColumn={{ md: "span 3" }}>
                    <NumberInput precision={2} step={0.01} min={0} value={item.unit_price} onChange={(val) => handleItemNumberChange(index, val, 'unit_price')}>
                      <NumberInputField bg={modalBg} borderRadius="md" placeholder="Price" />
                    </NumberInput>
                  </FormControl>
                </SimpleGrid>
                {/* --- UPDATE: FlowSense check of this line */}
                {item.item_id && checkByItem.get(String(item.item_id)) && (() => { const c = checkByItem.get(String(item.item_id)); return (
                  <HStack mt={2} spacing={2} fontSize="xs" flexWrap="wrap"><Icon as={MdAutoAwesome} color="teal.400" /><Text>{c.on_hand ?? '—'} in stock · {c.on_order} on order</Text>
                    {c.suggested !== null && <Text>· FlowSense suggests {c.suggested}</Text>}{c.cover_after_days !== null && <Text>· ≈ {c.cover_after_days} d cover after</Text>}
                    {c.reference_price !== null && <Text>· paid before {usd(c.reference_price)}</Text>}
                    {c.suggested > 0 && Number(item.quantity) !== c.suggested && <Button size="xs" variant="link" colorScheme="teal" onClick={() => handleItemNumberChange(index, c.suggested, 'quantity')}>use {c.suggested}</Button>}</HStack>); })()}
              </Box>
            ))}
            
            <Button leftIcon={<AddIcon />} onClick={addItem} variant="outline" colorScheme="blue" borderRadius="xl" borderStyle="dashed">
              Add Another Line Item
            </Button>
          </VStack>
        );

      case 2: // Budget & Justification
        return (
          <VStack spacing={5} align="stretch" as={motion.div} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <FormControl isRequired isInvalid={!!formErrors.budget_code}>
              <FormLabel fontWeight="bold">Budget Code / Vote</FormLabel>
              <Input 
                value={poData.budget_code} 
                onChange={(e) => updateField('budget_code', e.target.value)} 
                placeholder="e.g. MOW-WASH-2024" 
                bg={modalBg} 
                borderRadius="xl" 
                size="lg"
                list="po-create-budget-codes" // --- UPDATE
              />
              <datalist id="po-create-budget-codes">{(check?.budget_codes || []).map((b) => <option key={b} value={b} />)}</datalist>
              <FormErrorMessage>{formErrors.budget_code}</FormErrorMessage>
            </FormControl>

            <FormControl>
              <Flex justify="space-between" align="center" mb={2}>
                <FormLabel mb={0} fontWeight="bold">Formal Justification</FormLabel>
                <Button size="xs" colorScheme="purple" leftIcon={<Icon as={FaRobot} />} onClick={handleGenerateAIJustification} isLoading={isGeneratingAI} borderRadius="full">
                  Auto-Draft Memo
                </Button>
              </Flex>
              <Textarea 
                value={poData.notes} 
                onChange={(e) => updateField('notes', e.target.value)} 
                placeholder="Enter detailed reasoning for financial controller review..." 
                bg={modalBg} 
                rows={6} 
                borderRadius="xl" 
              />
            </FormControl>
          </VStack>
        );

      case 3: // Review
        const supplierName = suppliers.find(s => String(s.id) === String(poData.supplier_id))?.name;
        return (
          <VStack spacing={5} align="stretch" as={motion.div} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <Alert status="info" borderRadius="xl">
              <AlertIcon />
              <AlertDescription fontSize="sm">Please review the details. Submitting will encumber funds against the specified budget.</AlertDescription>
            </Alert>
            {/* --- UPDATE: FlowSense checks */}
            {(check?.warnings || []).length > 0 && (
              <Box p={3} bg={warnBg} borderRadius="xl" fontSize="sm" role="note" aria-label="FlowSense checks">
                <HStack mb={1}><Icon as={MdAutoAwesome} color="orange.400" /><Text fontWeight="bold">FlowSense checks</Text></HStack>
                {check.warnings.map((w, i) => <Text key={i}>• {w.text}</Text>)}
              </Box>
            )}

            <SimpleGrid columns={2} spacing={4} p={4} bg={sectionBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor}>
              <Box><Text fontSize="xs" color="gray.500">Supplier</Text><Text fontWeight="bold">{supplierName}</Text></Box>
              <Box><Text fontSize="xs" color="gray.500">Budget Vote</Text><Text fontWeight="bold">{poData.budget_code}</Text></Box>
              <Box><Text fontSize="xs" color="gray.500">Priority</Text><Badge colorScheme={{ low: 'gray', medium: 'blue', high: 'orange', critical: 'red' }[poData.priority] || 'gray'}>{poData.priority.toUpperCase()}</Badge></Box>{/* --- UPDATE: colour by priority (was always red) */}
              <Box><Text fontSize="xs" color="gray.500">Total Items</Text><Text fontWeight="bold">{items.length}</Text></Box>
            </SimpleGrid>

            <Box p={5} bg={highlightColor} borderRadius="xl" border="1px solid" borderColor="blue.200">
              <Flex justify="space-between" align="center">
                <HStack color="blue.700">
                  <Icon as={MdAttachMoney} boxSize={6} />
                  <Text fontWeight="bold">Total Financial Encumbrance</Text>
                </HStack>
                <Text fontSize="2xl" fontWeight="black" color="blue.700">
                  ${cartTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </Text>
              </Flex>
            </Box>
          </VStack>
        );
      default: return null;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="2xl" scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(8px)" />
      <ModalContent bg={modalBg} borderRadius="2xl">
        <Box h="5px" bgGradient={headerGradient} borderTopRadius="2xl" />
        
        <ModalHeader pb={0}>
          <HStack spacing={3} mb={4}>
            <Box p={2} bg="blue.100" borderRadius="lg">
              <Icon as={MdShoppingCart} color="blue.600" boxSize={5} />
            </Box>
            <Text fontSize="xl" fontWeight="bold">Procurement Wizard</Text>
          </HStack>

          <Stepper index={activeStep} colorScheme="blue" size="sm" mb={2}>
            {WIZARD_STEPS.map((step, index) => (
              <Step key={index}>
                <StepIndicator>
                  <StepStatus complete={<StepIcon />} incomplete={<StepNumber />} active={<StepNumber />} />
                </StepIndicator>
                <Box flexShrink='0' display={{ base: 'none', sm: 'block' }}>
                  <StepTitle>{step.title}</StepTitle>
                </Box>
                <StepSeparator />
              </Step>
            ))}
          </Stepper>
        </ModalHeader>
        
        <ModalCloseButton top={4} right={4} />
        
        <ModalBody py={6}>
          {/* --- UPDATE: FlowSense AI */}
          <Box mb={5}><FlowSenseInsightBar screen="po_wizard" compact data={null} ministryText={() => explainProcurementForMinistry(null)}
            headline={check ? [`${check.lines.length} line(s) checked · ${check.warnings.length} warning(s)`, check.supplier ? `${check.supplier.name}: grade ${check.supplier.supplier_grade || 'unrated'}` : null].filter(Boolean) : 'FlowSense checks each line as you add it.'}
            rulesAnswer={() => ['New purchase order — FlowSense checks:', ...(check?.warnings || []).map((w) => `• ${w.text}`), check && !check.warnings.length ? 'No warnings for this draft.' : null].filter(Boolean).join('\n')} /></Box>
          <AnimatePresence mode="wait">
            {renderStepContent()}
          </AnimatePresence>
        </ModalBody>
        
        <ModalFooter borderTopWidth="1px" borderColor={borderColor} bg={sectionBg} borderBottomRadius="2xl">
          <Flex w="full" justify="space-between">
            <Button variant="ghost" onClick={activeStep === 0 ? onClose : handleBack} borderRadius="xl">
              {activeStep === 0 ? 'Cancel' : 'Back'}
            </Button>
            
            {activeStep === WIZARD_STEPS.length - 1 ? (
              <Button colorScheme="blue" onClick={handleSubmit} isLoading={isSubmitting} leftIcon={<MdCheckCircle />} borderRadius="xl" px={8}>
                Submit Purchase Order
              </Button>
            ) : (
              <Button colorScheme="blue" onClick={handleNext} borderRadius="xl" px={8}>
                Next Step
              </Button>
            )}
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PurchaseOrderCreateModal;